/**
 * PERFORMANCE OPTIMIZATION: Collection Page Specific JavaScript
 *
 * This file contains JavaScript that ONLY runs on collection pages.
 * By splitting this from main.js, we reduce the initial bundle size for other pages.
 *
 * Expected TBT Reduction on non-collection pages: ~300-400ms
 *
 * Functionality included:
 * - Filter interactions enhancements
 * - Sort/toolbar enhancements
 * - Product grid lazy loading
 * - Collection pagination
 */

(function() {
  'use strict';

  /**
   * Enhanced Product Grid with Lazy Loading
   * Implements progressive loading for better performance on large collections
   */
  class ProductGridEnhancer {
    constructor() {
      this.grid = document.querySelector('[data-product-grid]');
      this.products = [];
      this.visibleProducts = new Set();
    }

    init() {
      if (!this.grid) return;

      this.products = Array.from(this.grid.querySelectorAll('.product-card'));

      // Set up intersection observer for product cards
      this.setupIntersectionObserver();

      // Enhance quick add buttons
      this.enhanceQuickAddButtons();

      console.log('[Collection Page] Product grid enhanced');
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.handleProductVisible(entry.target);
            }
          });
        },
        {
          rootMargin: '50px 0px',
          threshold: 0.1
        }
      );

      this.products.forEach(product => {
        observer.observe(product);
      });
    }

    handleProductVisible(productElement) {
      const productId = productElement.getAttribute('data-product-id');

      if (!this.visibleProducts.has(productId)) {
        this.visibleProducts.add(productId);

        // Load high-res images for visible products
        this.loadHighResImages(productElement);

        // Track product impression for analytics
        this.trackProductImpression(productElement);
      }
    }

    loadHighResImages(productElement) {
      const images = productElement.querySelectorAll('img[data-src]');
      images.forEach(img => {
        if (img.hasAttribute('data-src')) {
          img.src = img.getAttribute('data-src');
          img.removeAttribute('data-src');
        }
      });
    }

    trackProductImpression(productElement) {
      const productId = productElement.getAttribute('data-product-id');
      const productTitle = productElement.querySelector('.product-card__title')?.textContent;

      // Send to analytics
      if (window.dataLayer && productId) {
        window.dataLayer.push({
          event: 'view_item',
          ecommerce: {
            items: [{
              item_id: productId,
              item_name: productTitle
            }]
          }
        });
      }
    }

    enhanceQuickAddButtons() {
      const quickAddButtons = this.grid.querySelectorAll('[data-quick-add-btn]');

      quickAddButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleQuickAdd(button);
        });
      });
    }

    handleQuickAdd(button) {
      const productId = button.closest('.product-card').getAttribute('data-product-id');
      console.log('[Collection Page] Quick add triggered for product:', productId);

      // Add loading state
      button.classList.add('loading');

      // Quick add functionality is handled by quick-add.js
      // This just adds visual feedback
      setTimeout(() => {
        button.classList.remove('loading');
      }, 1000);
    }
  }

  /**
   * Filter Panel Enhancements
   * Adds better UX for filters with persistence and analytics
   */
  class FilterPanelEnhancer {
    constructor() {
      this.filterPanel = document.querySelector('[data-filter-panel]');
      this.activeFilters = new Set();
    }

    init() {
      if (!this.filterPanel) return;

      // Load saved filters from sessionStorage
      this.loadSavedFilters();

      // Track filter changes
      this.setupFilterTracking();

      console.log('[Collection Page] Filter panel enhanced');
    }

    loadSavedFilters() {
      try {
        const saved = sessionStorage.getItem('collection-filters');
        if (saved) {
          this.activeFilters = new Set(JSON.parse(saved));
        }
      } catch (e) {
        console.error('[Collection Page] Failed to load saved filters:', e);
      }
    }

    saveFilters() {
      try {
        sessionStorage.setItem('collection-filters', JSON.stringify([...this.activeFilters]));
      } catch (e) {
        console.error('[Collection Page] Failed to save filters:', e);
      }
    }

    setupFilterTracking() {
      const filterInputs = this.filterPanel.querySelectorAll('input[type="checkbox"]');

      filterInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          this.handleFilterChange(e.target);
        });
      });
    }

    handleFilterChange(input) {
      const filterValue = input.value;

      if (input.checked) {
        this.activeFilters.add(filterValue);
      } else {
        this.activeFilters.delete(filterValue);
      }

      this.saveFilters();

      // Track filter usage
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'filter_applied',
          filter_type: input.name,
          filter_value: filterValue,
          filter_active: input.checked
        });
      }

      console.log('[Collection Page] Filter changed:', filterValue, input.checked);
    }
  }

  /**
   * Infinite Scroll Enhancement
   * Implements smooth infinite scrolling for collections
   */
  class InfiniteScrollEnhancer {
    constructor() {
      this.container = document.querySelector('[data-product-grid]');
      this.loadMoreButton = document.querySelector('[data-load-more]');
      this.isLoading = false;
      this.currentPage = 1;
    }

    init() {
      if (!this.container || !this.loadMoreButton) return;

      // Set up intersection observer for load more button
      this.setupIntersectionObserver();

      console.log('[Collection Page] Infinite scroll enabled');
    }

    setupIntersectionObserver() {
      if (!('IntersectionObserver' in window)) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !this.isLoading) {
              this.loadMoreProducts();
            }
          });
        },
        {
          rootMargin: '200px 0px',
          threshold: 0
        }
      );

      observer.observe(this.loadMoreButton);
    }

    async loadMoreProducts() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.loadMoreButton.classList.add('loading');

      try {
        this.currentPage++;
        const url = new URL(window.location.href);
        url.searchParams.set('page', this.currentPage);

        const response = await fetch(url.toString());
        const html = await response.text();

        // Parse the HTML and extract products
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProducts = doc.querySelectorAll('[data-product-grid] .product-card');

        if (newProducts.length > 0) {
          // Append new products
          newProducts.forEach(product => {
            this.container.appendChild(product);
          });

          console.log('[Collection Page] Loaded', newProducts.length, 'more products');
        } else {
          // No more products, hide button
          this.loadMoreButton.style.display = 'none';
        }
      } catch (error) {
        console.error('[Collection Page] Failed to load more products:', error);
      } finally {
        this.isLoading = false;
        this.loadMoreButton.classList.remove('loading');
      }
    }
  }

  /**
   * Initialize all collection page enhancements
   */
  function initCollectionPageEnhancements() {
    // Initialize product grid enhancements
    const gridEnhancer = new ProductGridEnhancer();
    gridEnhancer.init();

    // Initialize filter panel enhancements
    const filterEnhancer = new FilterPanelEnhancer();
    filterEnhancer.init();

    // Initialize infinite scroll (if enabled)
    const infiniteScroll = new InfiniteScrollEnhancer();
    infiniteScroll.init();

    console.log('[Performance] Collection page enhancements loaded');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionPageEnhancements);
  } else {
    initCollectionPageEnhancements();
  }
})();
